import { ReactNode, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Flex, ToastContainer } from '@nilohealth/core-ui'
import Head from 'next/head'

import { useAuth, useSignOut } from '@auth/auth.hooks'
import { useSitemap } from '@dashboard/dashboard.hooks'
import { useCareProvider } from '@providers/providers.hooks'
import { usePermissions } from '@permissions/permissions.hooks'

import PermissionsProvider from '@permissions/permissions-provider'
import Navbar from '@navbar/navbar'
import AppSkeleton from './app.skeleton'

// this is set on newrelic setup on src/pages/_document.tsx
declare const newrelic: undefined | {
  setCustomAttribute: (name: string, value: string | number | null, persist?: boolean) => void,
  setCurrentRouteName: (name: string) => void,
}

type AppProps = {
  children: ReactNode
}
const App = ({ children }: AppProps) => {
  const { currentUser, isAuthorised, isLoading: loadingAuth } = useAuth()
  const { careProvider, isLoading: loadingProviders } = useCareProvider()
  const { permissions, isLoading: loadingPermissions } = usePermissions()

  useSitemap()

  const signOut = useSignOut()

  const router = useRouter()

  const careProviderId = careProvider?.id

  const currentUserId = currentUser?.id

  const routerPath = router.pathname || router.asPath

  useEffect(() => {
    if (typeof newrelic === 'undefined') return
    newrelic.setCustomAttribute('nilo.careProviderId', String(careProviderId), true)
  }, [careProviderId])

  useEffect(() => {
    if (typeof newrelic === 'undefined') return
    newrelic.setCustomAttribute('nilo.currentUserId', String(currentUserId), true)
  }, [currentUserId])

  useEffect(() => {
    if (typeof newrelic === 'undefined') return
    newrelic.setCurrentRouteName(routerPath)
  }, [routerPath])

  useEffect(() => {
    if (typeof currentUser === 'undefined' || (careProvider && currentUser?.careProvider !== careProvider.id)) {
      signOut.mutate()
    }
  }, [currentUser])

  if (!careProvider || loadingProviders || loadingPermissions || loadingAuth) {
    return <AppSkeleton />
  }
  return (
    <PermissionsProvider permissions={permissions}>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <meta name="description" content="Nilo analytics" />
        <title>Nilo Analytics</title>
      </Head>

      <Flex className="analytics-web" css={{ height: '100vh', width: '100vw' }}>
        {isAuthorised && <Navbar />}
        {children}
      </Flex>

      <ToastContainer />
    </PermissionsProvider>
  )
}

export default App
